import React, { useCallback, useEffect, useState } from 'react';
import ReduxToastr, { toastr } from 'react-redux-toastr';
import clsx from 'clsx';
import { Button, Hidden, IconButton, makeStyles } from '@material-ui/core';
import { AppBar } from '@/shared/app-bar/app-bar.component';
import { Routes } from '@/routes';
import { NavBarBottom } from '@/shared/app-bar/nav-bar-bottom.component';
import { NavBarContainer } from '@/shared/app-bar/tab-bar.container';
import { CasinoBonusesDto } from '@/models/CasinoLobbyV2/CasinoBonusesDto';
import { GetAmountWithDecimals } from '@/shared/functions/common';
import { PlayerSocketServiceClient } from '@/shared/services/socket-service';
import { SocketBasicMessageAdmin, SocketBasicRequest, SocketMessageChangeMaxBetAmount } from '@/models/Socket/socket-request';
import { EventActionEnum, SocketMessageEnum, SocketRooms } from '@/enum/socket/socket-enum';
import { DialogSocketMessagesComponent } from './dialogs/dialog-socket-messages.component';
import { ISocketIdentityStatusResponse, SocketMessagesByDateDto, SocketMessagesDto } from '@/models/Socket/socket-messages';
import { MessageServiceClient } from '@/shared/services/messages-service';
import { emptyCashierDialog, ICashierDialog } from '@/models/cashier-transaction';
import { TransactionTypeEnum } from '@/enum/transactions/TransactionTypeEnum';
import { ComponentEnum } from '@/enum/components/component-enum';
import { RouteSiteEnum } from '@/enum/routes/routes-site-enum';
import { ITvChannels } from '@/models/tv-channels';
import { SnackbarKey, useSnackbar } from 'notistack';
import { BitrixServiceClient } from '@/shared/services/bitrix-service';
import CloseIcon from '@material-ui/icons/Close';
import { IPlayerSession } from '@/models/player';
import { ICognitoCloseWindow, IIdentityDialog } from '@/models/users';
import { ITransactionContest } from '@/models/Cashier/Transactions/TransactionContest';
import { DialogTransactionContestComponent } from './dialogs/dialog-transaction-contest';
import { MarkerServiceClient } from '@/shared/services/marker-service';
import { getHistory } from '@/utils/history.util';
import { IAppSettingsDto } from '@/models/common';
import { getAppSettings } from '@/utils';
import { IMessageByUser, IMessageMarkAsRead } from '@/models/Messages/messages';
import MessageDialogComponent from '@/shared/app-bar/components/message-components/message-dialog.component';
import { convertPSTToUnix } from '../app-bar/components/ad-dialog.componenent';
import { getBannersAction } from '@/api/banners/actions/get-banners.action';
import { BannerDto } from '@/models/banner/banner.interface';

let logoutTimer: NodeJS.Timeout | null = null;

const ResetTimer = () => {
  try {
    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
    logoutTimer = setTimeout(() => {
      console.log('Inactivity Detected');
      window.parent.postMessage(EventActionEnum.LOG_OUT, '*');
    }, 3600000); //1 hour - 3600000
  } catch (error) {
    console.log('Console Log Error', error);
  }
};

const appBarHeight = '64px';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  content: {
    marginTop: appBarHeight,
    marginBottom: appBarHeight,
  },
  marginTop72: {
    marginTop: '82px',
    [theme.breakpoints.down('md')]: {
      marginTop: '-7px',
    },
  },
}));

const setCloseDialogPostMessage = () => {
  window.parent.postMessage(EventActionEnum.CLOSE_AD_DIALOG, '*');
};

const setEventListenerBitrix = (set: boolean) => {
  try {
    const elements = document.getElementsByClassName('b24-widget-button-wrapper');

    if (elements.length > 0) {
      const element = elements[0];
      if (set) {
        element.addEventListener('click', setCloseDialogPostMessage);
      } else {
        element.removeEventListener('click', setCloseDialogPostMessage);
      }
    }
  } catch (error) {
    console.log('setEventListenerBitrix', error);
  }
};

const isMobile: boolean = window.innerWidth <= 768;

export const getCurrentPage = () => {
  return window.location.href.split(window.location.origin)[1].replace('/#/', '/');
};

export interface AppRootProps {
  isLoggedIn: boolean;
  user: any;
  userBalance: any;
  tvOnOffResponses: boolean;
  path: string;
  tourSteps: any;
  rightOpen: boolean;
  rollover: any;
  isLoading: boolean;
  casinoBonuses: CasinoBonusesDto[];
  casinoBonus: CasinoBonusesDto | null;
  cashierDialog: ICashierDialog;
  socketMessages: SocketMessagesDto[];
  transactionType: number | null;
  socketMessagesByDate: SocketMessagesByDateDto[];
  tvChannelsList: ITvChannels[];
  runTour: boolean;
  identityDialogOpen: IIdentityDialog;
  shareable_url: string | null;
  transaction_contest: ITransactionContest[];
  openDialogContest: boolean;
  app_settings: IAppSettingsDto;
  messages: IMessageByUser[];
  isLoadingMessages: boolean;
  banners: BannerDto[];
}
export interface ExternalActionProps {
  GetMessagesByUserAction: () => void;
  GetTransactionContestAction: () => void;
  MarkNotifiedAsReadPlayerAction: (data: SocketMessagesDto) => void;
  MarkNotifiedAsReadPlayerByDateAction: (data: SocketMessagesByDateDto) => void;
  MarkTourAsDisplayedAction: () => void;
  SetAppSettingAction: (appSettings: IAppSettingsDto) => void;
  SetChatInitializedAction: (data: boolean) => void;
  SetMaxBetAmountAction: (data: SocketMessageChangeMaxBetAmount) => void;
  SetOpenDialogTransactionContestAction: (data: boolean) => void;
  SetRights: (data: ISocketIdentityStatusResponse) => void;
  SetRunTourAction: (data: boolean) => void;
  SetSocketMessagesPlayerAction: (data: SocketMessagesDto[]) => void;
  SetSocketMessagesPlayerByDateAction: (data: SocketMessagesByDateDto[]) => void;
  UpdateMessagesByUserMarkAsReadAction: (data: IMessageMarkAsRead) => void;
  changeUserBalanceAction: (data: any) => void;
  clearCognitoUrlAction: () => void;
  closeCognitoFlowSessionAction: (data: ICognitoCloseWindow) => void;
  createCognitoRetryAction: () => void;
  getPlayerRolloverAction: (x: any) => void;
  getTourStepsAction: () => void;
  getUserBalanceAction: () => void;
  getUserInfoAction: () => void;
  logoutAction: () => void;
  ptpiGetCasinoBonusesAction: () => void;
  setCashierDialogAction: (data: any) => void;
  setTvUrlAction: (ch: string) => void;
  toggleVerifyIdentityDialogAction: (state: IIdentityDialog) => void;
  tvOnOffAction: (isOn: boolean) => void;
  getBannersAction: () => void;
}

export const AppRoot = ({
  app_settings,
  cashierDialog,
  identityDialogOpen,
  isLoading,
  isLoadingMessages,
  isLoggedIn,
  messages,
  openDialogContest,
  path,
  rightOpen,
  rollover,
  runTour,
  shareable_url,
  socketMessages,
  socketMessagesByDate,
  tourSteps,
  transactionType,
  transaction_contest,
  tvChannelsList,
  tvOnOffResponses,
  user,
  userBalance,
  GetMessagesByUserAction,
  GetTransactionContestAction,
  MarkNotifiedAsReadPlayerAction,
  MarkNotifiedAsReadPlayerByDateAction,
  MarkTourAsDisplayedAction,
  SetAppSettingAction,
  SetChatInitializedAction,
  SetMaxBetAmountAction,
  SetOpenDialogTransactionContestAction,
  SetRights,
  SetRunTourAction,
  SetSocketMessagesPlayerAction,
  SetSocketMessagesPlayerByDateAction,
  UpdateMessagesByUserMarkAsReadAction,
  changeUserBalanceAction,
  clearCognitoUrlAction,
  closeCognitoFlowSessionAction,
  createCognitoRetryAction,
  getPlayerRolloverAction,
  getTourStepsAction,
  getUserBalanceAction,
  getUserInfoAction,
  setCashierDialogAction,
  setTvUrlAction,
  toggleVerifyIdentityDialogAction,
  tvOnOffAction,
  getBannersAction,
  banners,
}: ExternalActionProps & AppRootProps) => {
  const classes = useStyles();

  const [open, setOpen] = useState<boolean>(false);
  const [request, setRequest] = useState<SocketBasicMessageAdmin | null>(null);
  const [cashierSettings, setCashierSettings] = useState<ICashierDialog>(emptyCashierDialog);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [displayAdd, setDisplayAdd] = useState<boolean>(true);
  const [openDialogMessages, setOpenDialogMessages] = React.useState(false);
  const [unreadMessages, setUnreadMessages] = React.useState<number>(0);

  useEffect(() => {
    const localUnreadMessages = messages.filter(x => x.viewed_at == null).length;
    setUnreadMessages(localUnreadMessages < 0 ? 0 : localUnreadMessages);
  }, [messages]);

  useEffect(() => {
    const local_host = window.location.origin;
    const host_name = local_host.replace('http://', '').replace('https://', '');
    const host = (getAppSettings().hosts ?? []).find((x: IAppSettingsDto) => x.site.url == host_name);
    if (host?.site?.name && host?.site?.url && host?.logo?.url && host?.theme?.url && host?.signup?.url) {
      SetAppSettingAction(host);
    }
  }, [getAppSettings().hosts]);

  useEffect(() => {
    if (app_settings?.theme?.url) {
      document.getElementById('css_url')?.setAttribute('href', app_settings.theme.url);
      document.getElementById('favicon_id')?.setAttribute('href', `//${app_settings.site.url}/${app_settings.logo.url}`);
    }
  }, [app_settings]);

  useEffect(() => {
    //add event listeners for inactivity
    document.addEventListener('mousemove', ResetTimer);
    document.addEventListener('keypress', ResetTimer);
    document.addEventListener('scroll', ResetTimer);
    document.addEventListener('click', ResetTimer);

    ResetTimer();

    //Remove all event listeners
    return () => {
      if (logoutTimer) {
        clearTimeout(logoutTimer);
      }
      document.removeEventListener('mousemove', ResetTimer);
      document.removeEventListener('keypress', ResetTimer);
      document.removeEventListener('scroll', ResetTimer);
      document.removeEventListener('click', ResetTimer);
    };
  }, []);

  const handleOpenChat = useCallback(async () => {
    try {
      await BitrixServiceClient.initAndOpenChat();
    } catch (err) {
      console.error((err as Error).message);
    }
  }, []);

  const action = (snackbarId: SnackbarKey, request: SocketBasicMessageAdmin) => (
    <>
      {request.chat_message && request.message.trim().length > 0 && (
        <Button variant='contained' color='primary' style={{ marginRight: '10px' }} onClick={handleOpenChat}>
          Open Chat
        </Button>
      )}

      <IconButton
        aria-label='delete'
        size='small'
        onClick={() => {
          closeSnackbar(snackbarId);
        }}
      >
        <CloseIcon fontSize='small' />
      </IconButton>
    </>
  );

  useEffect(() => {
    MessageServiceClient.SetMessage(socketMessages);
  }, [socketMessages]);

  const whiteListCashierPage = [
    RouteSiteEnum.CASHIER_METHOD.toString(),
    RouteSiteEnum.CASHIER_BONUS.toString(),
    RouteSiteEnum.CASHIER_COMPLETE.toString(),
    RouteSiteEnum.CASHIER_CONFIRM.toString(),
    RouteSiteEnum.CASHIER_INFO.toString(),
    RouteSiteEnum.CASHIER_PAYMENT.toString(),
    RouteSiteEnum.CASHIER_BANK.toString(),
  ];

  useEffect(() => {
    MessageServiceClient.SetMessageByDate(socketMessagesByDate);
  }, [socketMessagesByDate]);

  useEffect(() => {
    const timeOutMinute = setInterval(() => {
      const currentDate = new Date();
      const pst = currentDate.toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
      });
      const pstDate = new Date(pst);

      MessageServiceClient.CheckTimeToDisplayMessage(pstDate);
    }, 6000);

    const startDate = convertPSTToUnix('2025-02-08 21:00');
    const endDate = convertPSTToUnix('2025-02-09 05:55');
    const currentTime = Date.now();

    // Casino maintenance message
    if (startDate > 0 && endDate > 0 && currentTime >= startDate && currentTime <= endDate) {
      window.parent.postMessage(
        {
          data: {
            data: {
              id: '',
              action: SocketMessageEnum.SHOWDIALOG,
              message: `
                Dear VIP Player,
                <br/>
                <br/>
                Instant Withdrawals will be temporarily unavailable due to scheduled maintenance on February 8, 2025, from 11 PM to 6 AM (PST).
                <br/>
                We appreciate your patience and apologize for any inconvenience. 
                <br/>
                Thank you for understanding!
                <br/>
                <br/>
                VIP Team.`,
              room: '',
              chat_message: '',
              showOpenDialogButton: false,
            },
          },
          action: 'message-to-client',
        },
        '*',
      );
    }
    return () => {
      clearInterval(timeOutMinute);
    };
  }, []);

  useEffect(() => {
    let currPage = getCurrentPage();

    if (cashierDialog.isShow) {
      currPage = '/cashier';
      if (cashierDialog.transactionType != cashierSettings.transactionType && transactionType != null && transactionType != -1) {
        const depositPage = socketMessages.find(x => x.user_perm_component_id == ComponentEnum.CASHIER_DEPOSITS);
        const wdPage = socketMessages.find(x => x.user_perm_component_id == ComponentEnum.CASHIER_WITHDRAWALS);
        switch (cashierDialog.transactionType) {
          case TransactionTypeEnum.DEPOSIT:
            if (depositPage) currPage = depositPage.component.route;
            break;
          case TransactionTypeEnum.WITHDRAWAL:
            if (wdPage) currPage = wdPage.component.route;
            break;
          default:
            break;
        }
      }
    }

    setCashierSettings(cashierDialog);
    MessageServiceClient.SetMessageToast(currPage);
  }, [cashierDialog]);

  useEffect(() => {
    setTimeout(() => {
      let currPage = getCurrentPage();
      if (!cashierDialog.isShow && transactionType != null && transactionType != -1) {
        currPage = '/cashier';
        const depositPage = socketMessages.find(x => x.user_perm_component_id == ComponentEnum.CASHIER_DEPOSITS);
        const wdPage = socketMessages.find(x => x.user_perm_component_id == ComponentEnum.CASHIER_WITHDRAWALS);
        switch (transactionType) {
          case TransactionTypeEnum.DEPOSIT:
            if (depositPage) currPage = depositPage.component.route;
            break;
          case TransactionTypeEnum.WITHDRAWAL:
            if (wdPage) currPage = wdPage.component.route;
            break;
          default:
            break;
        }
        MessageServiceClient.SetMessageToast(currPage);
      }
    }, 500);
  }, [transactionType]);

  const TourDisplayCheck = (currPage: string) => {
    SetRunTourAction(false);
    let show = false;
    try {
      const tour_display = localStorage.getItem(`tour_display_${user?.username}`);
      if (tour_display) {
        const tmpPage = currPage == '/' ? RouteSiteEnum.SPORTSBOOK : currPage;
        const pages: string[] = JSON.parse(localStorage.getItem(`tour_steps_displayed_${user?.username}`) ?? '[]');
        const pageSelected = pages.find(x => x == tmpPage);
        if (!pageSelected) {
          show = true;
          pages.push(tmpPage);
          localStorage.setItem(`tour_steps_displayed_${user?.username}`, JSON.stringify(pages));
          if (parseInt(tour_display) == 0) {
            MarkTourAsDisplayedAction();
          }
        }
      }
    } catch (error) {
      console.error('Set Page', error);
    }
    setTimeout(() => {
      SetRunTourAction(show);
    }, 1500);
  };

  const ShowAndHideMarkerByRoute = (route: string) => {
    if (route.includes('/#')) {
      route = route.replace('/#', '');
    }
    if (isMobile) {
      MarkerServiceClient.hide();
    } else {
      if (['/live-sports'].indexOf(route) == -1) {
        MarkerServiceClient.hide();
      } else {
        MarkerServiceClient.hide();
      }
    }
  };

  useEffect(() => {
    TourDisplayCheck(getCurrentPage());

    setTimeout(() => {
      const Session: SocketBasicRequest = {
        accessToken: user && user.token && user.token.toString().trim().length > 0 ? user.token : null,
        userID: user && user.username && user.username.toString().trim().length > 0 ? user.username : SocketRooms.VIPPUBLIC,
      };

      window.addEventListener('hashchange', function () {
        setTimeout(() => {
          const currPage = getCurrentPage();
          TourDisplayCheck(currPage);
          PlayerSocketServiceClient(Session).UpdateInformation({
            currentPage: window.location.href.split(window.location.origin)[1],
            updated: true,
            updatedAt: new Date(),
          });
          if (whiteListCashierPage.indexOf(currPage) == -1) MessageServiceClient.SetMessageToast(currPage);
          ShowAndHideMarkerByRoute(currPage);
          MarkerServiceClient.setCustomData();
        }, 1000);
      });

      window.addEventListener('locationchange', function () {
        setTimeout(() => {
          const currPage = getCurrentPage();
          TourDisplayCheck(currPage);
          PlayerSocketServiceClient(Session).UpdateInformation({
            currentPage: window.location.href.split(window.location.origin)[1],
            updated: true,
            updatedAt: new Date(),
          });

          MessageServiceClient.SetMessageToast(currPage);
          MarkerServiceClient.setCustomData();
          ShowAndHideMarkerByRoute(currPage);
        }, 1000);
      });
    }, 3000);
  }, []);

  const MarkAsRead = (data: SocketMessagesDto) => {
    MarkNotifiedAsReadPlayerAction(data);
  };

  const handlerDialog = () => {
    setRequest(null);
    setOpen(false);
  };

  const handleIdentityVerification = (data: ISocketIdentityStatusResponse) => {
    if (data.is_success == true) {
      setTimeout(() => {
        toggleVerifyIdentityDialogAction({
          open: true,
          message: 'YOUR ACCOUNT HAS BEEN VERIFIED',
          result: true,
          playerProfileSettingsPaymentMethodBonuses: data.player_profile_settings_payment_method_bonuses || [],
        });
        if (data.rights) {
          try {
            getUserInfoAction();
            const user: IPlayerSession = JSON.parse(localStorage.getItem('user') ?? '{}');
            user.rights = data.rights;
            localStorage.setItem('user', JSON.stringify(user));
            SetRights(data);
          } catch (error) {
            console.log('erros');
          }
        }
      }, 2000);
    } else {
      toggleVerifyIdentityDialogAction({
        open: true,
        message: data.message,
        result: false,
        playerProfileSettingsPaymentMethodBonuses: data.player_profile_settings_payment_method_bonuses || [],
      });
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      userInfo();
      MarkerServiceClient.init();
    }

    window.addEventListener('onBitrixLiveChat', function () {
      window.parent.postMessage('bitrix_live_chat_initiated', '*');
    });

    //Here detect all message from iframes
    window.addEventListener(
      'message',
      event => {
        //console.log(event);
        if (event.origin.includes('plive.webavanx.com'))
          if (event.data && (event.data == 'bet-accepted' || event.data['bet-group-rejected'])) getUserBalanceAction();
        // if (event.data?.exi_fMessageType_str == 'exi_onGameRoundFinalized') getUserBalanceAction();
        if (event.data == 'vip_update_balance_pusher') getUserBalanceAction();
        if (event.data == 'bitrix_live_chat_initiated') {
          SetChatInitializedAction(true);
          if (isMobile) {
            setEventListenerBitrix(true);
          }
          console.log('bitrix_live_chat_initiated  x2');
        }
        if (event.data == EventActionEnum.OPEN_IDENTITY_DIALOG) {
          setCashierDialogAction(emptyCashierDialog);
          toggleVerifyIdentityDialogAction({
            open: true,
            message: '',
            result: false,
            playerProfileSettingsPaymentMethodBonuses: [],
          });
        }

        if (event.data == EventActionEnum.GO_TO_PROFILE) {
          setCashierDialogAction(emptyCashierDialog);
          getHistory().push('/profile');
        }

        if (event.data == EventActionEnum.PLACE_BET_FROM_CASHIER_DIALOG) {
          setCashierDialogAction(emptyCashierDialog);
          getHistory().push('/sportsbook');
        }

        if (event.data == EventActionEnum.CLOSE_AD_DIALOG) {
          setDisplayAdd(false);
          setEventListenerBitrix(false);
        }

        if (event.data == 'hide_dialog') {
          MarkerServiceClient.hide();
        }

        if (event.data == EventActionEnum.LOG_OUT) {
          if (localStorage.getItem('user') != null && localStorage.getItem('token') != null) {
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            localStorage.removeItem('login_process');
            location.href = '/';
          }
        }

        if (event.data && event.data.data && event.data.action) {
          switch (event.data.action) {
            case EventActionEnum.BALANCE_CHANGE: {
              let timeout = 0;
              if (GetAmountWithDecimals(userBalance.RealAvailBalance, 2) < GetAmountWithDecimals(event.data.data.RealAvailBalance, 2))
                timeout = 2500;

              setTimeout(() => {
                changeUserBalanceAction(event.data.data);
              }, timeout);
              break;
            }
            case EventActionEnum.MESSAGE_TO_CLIENT: {
              handlerMessageClient({
                ...event.data.data.data,
                showOpenDialogButton: event.data.data.data.showOpenDialogButton ? true : false,
              });
              break;
            }
            case EventActionEnum.SET_MESSAGES:
            case EventActionEnum.GET_MESSAGES_RECEIVED: {
              SetSocketMessagesPlayerAction(event.data.data.data);
              break;
            }

            case EventActionEnum.SET_MESSAGES_BY_DATE:
              SetSocketMessagesPlayerByDateAction(event.data.data.data);
              break;

            case EventActionEnum.MARK_AS_READ_MESSAGE: {
              MarkAsRead(event.data.data);
              break;
            }

            case EventActionEnum.MARK_AS_READ_MESSAGE_BY_DATE: {
              MarkNotifiedAsReadPlayerByDateAction(event.data.data);
              break;
            }

            case EventActionEnum.SIGNUP_STATUS: {
              handleIdentityVerification(event.data.data);
              break;
            }

            case EventActionEnum.CLOSE_COGNITO_IDENTITY: {
              closeCognitoFlowSessionAction(event.data.data);
              break;
            }

            case EventActionEnum.MESSAGE_TO_CLIENT_API: {
              handlerMessageClient({
                ...event.data.data,
                showOpenDialogButton: event.data.data.showOpenDialogButton ? true : false,
              });
              break;
            }

            case EventActionEnum.MESSAGE_NOTIFICATION: {
              GetMessagesByUserAction();
              break;
            }

            case EventActionEnum.CALL_TOUR: {
              const currPage = getCurrentPage();
              TourDisplayCheck(currPage);
              break;
            }

            case EventActionEnum.UPDATE_MAX_WAGER: {
              SetMaxBetAmountAction(event.data.data);
              break;
            }

            case EventActionEnum.SET_CUSTOM_DATA: {
              MarkerServiceClient.setCustomData();
              break;
            }
            default:
              break;
          }
        }
      },
      false,
    );
    // if (Object.entries(userBalance).length === 0 && isLoggedIn) {
    //   getUserBalanceAction();
    // }

    RemoveFbclid();
  }, []);
  const userInfo = async () => {
    getUserInfoAction();
  };

  const handlerMessageClient = (request: SocketBasicMessageAdmin) => {
    switch (request.action) {
      case SocketMessageEnum.MESSAGE:
        toastr.warning('VIP TEAM MESSAGE.', request.message, { timeOut: 8000 });
        break;
      case SocketMessageEnum.SHOWDIALOG:
        setRequest(request);
        setOpen(true);
        break;
      case SocketMessageEnum.SNACKBAR:
        enqueueSnackbar(request.message, {
          variant: 'default',
          autoHideDuration: 100000,
          action: key => action(key, request),
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      const d = document.getElementsByClassName('b24-widget-button-inner-container')[1];
      if (d?.parentNode) {
        d.parentNode.removeChild(d);
      }

      getUserBalanceAction();
    }
  }, [path]);

  //Connect to socket for instant notifications
  useEffect(() => {
    setTimeout(() => {
      let userID: string = SocketRooms.VIPPUBLIC;
      let accessToken = null;

      try {
        const userLocalStorage = localStorage.getItem('user');
        if (userLocalStorage) {
          const userLocal: IPlayerSession = JSON.parse(userLocalStorage);
          userID = userLocal.username ? userLocal.username : user.username ? user.username : userID;
          accessToken = userLocal.token;
        }
      } catch (error) {
        console.error('Get User', { error });
      }

      if (isLoggedIn && userID == SocketRooms.VIPPUBLIC) {
        userID = user.username;
        accessToken = user.token;
      }

      PlayerSocketServiceClient({ userID, accessToken }).CreateSocket({ userID, accessToken });
    }, 5000);
  }, []);

  const RemoveFbclid = () => {
    const currentHref = window.location.href;
    if (!currentHref) return;
    if (typeof currentHref !== 'string') return;

    const questionmarkIndex = currentHref.indexOf('?');
    if (questionmarkIndex === -1) return;

    const url = currentHref.substring(0, questionmarkIndex);
    const hashIndex = currentHref.indexOf('#');

    const query =
      hashIndex !== -1
        ? currentHref.substr(questionmarkIndex + 1, hashIndex - questionmarkIndex - 1)
        : currentHref.substr(questionmarkIndex + 1);

    const hash = hashIndex !== -1 ? currentHref.substr(hashIndex + 1) : undefined;

    const params = query.split('&').filter(param => !param.startsWith('fbclid='));

    const newHref = url + (params.length ? '?' + params.join('&') : '') + (hash !== undefined ? '#' + hash : '');
    if (currentHref === newHref) return;

    if (window.history && window.history.replaceState) {
      window.history.replaceState(undefined, '', newHref);
    } else {
      window.location.replace(newHref);
    }
  };

  return (
    <div className={classes.root}>
      {request && open && <DialogSocketMessagesComponent handlerDialog={handlerDialog} open={open} request={request} />}
      {openDialogContest && (
        <DialogTransactionContestComponent
          GetTransactionContestAction={GetTransactionContestAction}
          data={transaction_contest}
          handlerDialog={() => SetOpenDialogTransactionContestAction(false)}
          isLoading={isLoading}
          open={openDialogContest}
        />
      )}
      {isLoggedIn && (
        <AppBar
          user={user}
          userBalance={userBalance}
          tvOnOffResponses={tvOnOffResponses}
          tvOnOffAction={tvOnOffAction}
          setTvUrlAction={setTvUrlAction}
          isTvOn={tvOnOffResponses}
          getTourStepsAction={getTourStepsAction}
          tourSteps={tourSteps}
          rollover={rollover}
          displayAdd={displayAdd}
          getPlayerRolloverAction={getPlayerRolloverAction}
          setCashierDialogAction={setCashierDialogAction}
          cashierDialog={cashierDialog}
          tvChannelsList={tvChannelsList}
          isLoading={isLoading}
          runTour={runTour}
          SetRunTourAction={SetRunTourAction}
          toggleVerifyIdentityDialogAction={toggleVerifyIdentityDialogAction}
          identityDialogOpen={identityDialogOpen}
          shareable_url={shareable_url}
          createCognitoRetryAction={createCognitoRetryAction}
          clearCognitoUrlAction={clearCognitoUrlAction}
          GetMessagesByUserAction={GetMessagesByUserAction}
          setOpenDialogMessages={setOpenDialogMessages}
          unreadMessages={unreadMessages}
          getBannersAction={getBannersAction}
          banners={banners}
        />
      )}

      <div
        className={clsx({
          [classes.content]: isLoggedIn,
        })}
      >
        <Hidden mdDown>{isLoggedIn && <NavBarContainer />}</Hidden>
        <Hidden lgUp>
          {isLoggedIn && (
            <NavBarBottom
              user={user}
              rightOpen={rightOpen}
              SetRunTourAction={SetRunTourAction}
              setOpenDialogMessages={setOpenDialogMessages}
              unreadMessages={unreadMessages}
            />
          )}
        </Hidden>
        <div
          className={clsx({
            [classes.marginTop72]: isLoggedIn,
          })}
        >
          <Routes />
        </div>
      </div>

      {openDialogMessages && (
        <MessageDialogComponent
          GetMessagesByUserAction={GetMessagesByUserAction}
          UpdateMessagesByUserMarkAsReadAction={UpdateMessagesByUserMarkAsReadAction}
          messages={messages}
          isLoadingMessages={isLoadingMessages}
          user={user}
          onClose={() => {
            setOpenDialogMessages(false);
          }}
        />
      )}
      <ReduxToastr timeOut={2000} transitionIn='fadeIn' transitionOut='fadeOut' />
    </div>
  );
};
